import { apiClient, handleApiRequest } from './client'

export interface BillingInvoice {
  id: string
  number: string
  school_id: string
  school_name?: string | null
  period_year: number
  period_month: number
  currency: string
  user_count: number
  list_unit_gross: string | number
  deal_percent: string | number
  unit_gross: string | number
  net: string | number
  vat_rate: string | number
  vat_amount: string | number
  gross: string | number
  remaining: string | number
  status: string
  collection_mode: string
  due_at: string
  paid_at?: string | null
  paid_via?: string | null
  qr_reference?: string | null
  has_ticket?: boolean
  commission_rate_snapshot?: string | number | null
  commission_amount?: string | number | null
  ticket_lifetime?: number | null
  lines?: Array<{
    user_id?: string | null
    display_name?: string | null
    fraction: string | number
    line_gross: string | number
    kind: string
  }>
}

export interface BillingPrice {
  currency: string
  unit_price_gross: string | number
  fx_index_enabled: boolean
  anchor_usd_per_unit?: string | number | null
}

export interface BillingSettings {
  legal_name: string
  address?: string | null
  zip_code?: string | null
  city?: string | null
  country: string
  iban?: string | null
  qr_besr_id?: string | null
  vat_number?: string | null
  due_days: number
  reminder_2_days: number
  lock_days_after_reminder_2: number
  auto_lock_enabled: boolean
  debit_retry_hours: number
  ticket_pause_days: number
  fx_threshold_percent: string | number
  deal_cap_percent: string | number
  invoice_seq: number
}

export interface BillingMe {
  payer_user_id?: string | null
  collection_mode: string
  mandate_status: string
  currency?: string | null
  deal_percent?: string | number
  is_superadmin?: boolean
  payer_candidates?: Array<{ id: string; email?: string; name: string; role: string }>
  lock_warning?: string | null
  invoices?: BillingInvoice[]
  stripe?: boolean
  paypal?: boolean
}

export const billingApi = {
  me: () => handleApiRequest<BillingMe>(apiClient.get('/api/v1/billing/me')),
  setPayer: (user_id: string) => handleApiRequest(apiClient.post('/api/v1/billing/payer', { user_id })),
  setMode: (mode: string) => handleApiRequest(apiClient.post('/api/v1/billing/collection-mode', { mode })),
  dispute: (id: string) => handleApiRequest(apiClient.post(`/api/v1/billing/invoices/${id}/dispute`)),
  stripeCheckout: (invoice_id: string) =>
    handleApiRequest<{ url: string }>(apiClient.post('/api/v1/billing/stripe/checkout', { invoice_id })),
  paypalCheckout: (invoice_id: string) =>
    handleApiRequest<{ url: string }>(apiClient.post('/api/v1/billing/paypal/checkout', { invoice_id })),
  stripeMandate: () => handleApiRequest<{ url: string }>(apiClient.post('/api/v1/billing/stripe/mandate-setup')),
  paypalMandate: () => handleApiRequest<{ url: string }>(apiClient.post('/api/v1/billing/paypal/mandate-setup')),
  paypalMandateComplete: () => handleApiRequest<{ mandate_status: string }>(apiClient.post('/api/v1/billing/paypal/mandate-complete')),
  downloadPdf: (id: string) =>
    apiClient.get(`/api/v1/billing/invoices/${id}/pdf`, { responseType: 'blob' }).then((r) => {
      const url = URL.createObjectURL(r.data)
      window.open(url, '_blank')
    }),
}

export const ultraadminBillingApi = {
  getSettings: () => handleApiRequest<BillingSettings>(apiClient.get('/api/v1/ultraadmin/billing/settings')),
  putSettings: (data: Partial<BillingSettings>) =>
    handleApiRequest<BillingSettings>(apiClient.put('/api/v1/ultraadmin/billing/settings', data)),
  vatRates: () => handleApiRequest(apiClient.get('/api/v1/ultraadmin/billing/vat-rates')),
  putVat: (country_code: string, rate_percent: number) =>
    handleApiRequest(apiClient.put('/api/v1/ultraadmin/billing/vat-rates', { country_code, rate_percent })),
  prices: () => handleApiRequest<BillingPrice[]>(apiClient.get('/api/v1/ultraadmin/billing/prices')),
  putPrice: (currency: string, unit_price_gross: number, fx_index_enabled: boolean) =>
    handleApiRequest<BillingPrice>(apiClient.put('/api/v1/ultraadmin/billing/prices', { currency, unit_price_gross, fx_index_enabled })),
  deal: (schoolId: string, percent: number) =>
    handleApiRequest<{ deal_percent: string | number }>(
      apiClient.put(`/api/v1/ultraadmin/billing/schools/${schoolId}/deal`, { percent }),
    ),
  invoices: (params?: Record<string, string>) =>
    handleApiRequest<BillingInvoice[]>(apiClient.get('/api/v1/ultraadmin/billing/invoices', { params })),
  invoice: (id: string) => handleApiRequest<BillingInvoice>(apiClient.get(`/api/v1/ultraadmin/billing/invoices/${id}`)),
  markPaid: (id: string, amount: number, note?: string, received_at?: string) =>
    handleApiRequest(apiClient.post(`/api/v1/ultraadmin/billing/invoices/${id}/mark-paid`, { amount, note, received_at })),
  downloadPdf: (id: string) =>
    apiClient.get(`/api/v1/ultraadmin/billing/invoices/${id}/pdf`, { responseType: 'blob' }).then((r) => {
      const url = URL.createObjectURL(r.data)
      window.open(url, '_blank')
    }),
  credit: (id: string, amount: number, reason?: string) =>
    handleApiRequest(apiClient.post(`/api/v1/ultraadmin/billing/invoices/${id}/credit`, { amount, reason })),
  reissue: (id: string) =>
    handleApiRequest(apiClient.post(`/api/v1/ultraadmin/billing/invoices/${id}/reissue`)),
  tickets: () => handleApiRequest(apiClient.get('/api/v1/ultraadmin/billing/tickets')),
  resolveTicket: (id: string, accepted: boolean) =>
    handleApiRequest(apiClient.post(`/api/v1/ultraadmin/billing/tickets/${id}/resolve`, { accepted })),
  overdue: () => handleApiRequest(apiClient.get('/api/v1/ultraadmin/billing/overdue')),
}

export const partnerBillingApi = {
  invoices: () => handleApiRequest<BillingInvoice[]>(apiClient.get('/api/v1/partner/billing/invoices')),
  overdue: () => handleApiRequest(apiClient.get('/api/v1/partner/billing/overdue')),
  deal: (schoolId: string, percent: number) =>
    handleApiRequest(apiClient.put(`/api/v1/partner/schools/${schoolId}/deal`, { percent })),
}

export const publicPayApi = {
  get: (token: string) => handleApiRequest(apiClient.get(`/api/v1/billing/pay/${token}`)),
  stripe: (token: string) => handleApiRequest<{ url: string }>(apiClient.post(`/api/v1/billing/pay/${token}/stripe`)),
  paypal: (token: string) => handleApiRequest<{ url: string }>(apiClient.post(`/api/v1/billing/pay/${token}/paypal`)),
}
